/**
 * 연락처 정규화·검증 (스키마 폼 공용).
 *
 * PHP 동일 구현: `qrshop_normalize_phone()` / `qrshop_is_valid_phone()`
 * (inc/helpers/template-functions-salon.php). 규칙이 갈라지면 클라이언트는
 * 통과하는데 서버가 400 을 내는 상황이 생기므로 둘을 함께 고친다.
 *
 * 전화번호는 발송용이 아니라 개인 식별 키라서, 같은 사람이 언제 적어도 같은
 * 문자열이 나오는 표준형이 목적이다.
 *   +82 10-1234-5678 / 010-1234-5678 → 01012345678
 *   +1 (415) 555-0123                → +14155550123
 */

// `+` 1자리 + E.164 최대 15자리.
const MAX_LENGTH = 16;

/** 입력 중 필터 — 숫자와 맨 앞 `+` 한 자리만 남긴다(정규화는 하지 않는다). */
export function sanitizePhoneInput(value) {
  const raw = String(value ?? '');
  const plus = raw.trimStart().startsWith('+') ? '+' : '';

  return (plus + raw.replace(/\D/g, '')).slice(0, MAX_LENGTH);
}

/** 표준형으로 정규화한다. 규칙은 파일 상단 주석 참고. */
export function normalizePhone(value) {
  let raw = String(value ?? '').trim();

  if (raw === '') return '';

  let isIntl = false;

  if (raw.startsWith('+')) {
    isIntl = true;
    raw = raw.slice(1);
  }

  let digits = raw.replace(/\D/g, '');

  if (digits === '') return '';

  // 00 은 국제전화 접속번호 — `+` 와 같은 뜻이다.
  if (!isIntl && digits.startsWith('00')) {
    isIntl = true;
    digits = digits.slice(2);
  }

  // 한국 번호는 국제형으로 적었어도 국내형으로 되돌린다(기존 데이터와 같은 키).
  if (isIntl && digits.startsWith('82')) {
    const rest = digits.slice(2);

    if (rest !== '') {
      digits = rest.startsWith('0') ? rest : `0${rest}`;
      isIntl = false;
    }
  }

  if (digits === '') return '';

  return isIntl ? `+${digits}` : digits;
}

/** 정규화 후 형식 검증. 국내는 휴대폰만, 해외는 국가번호 포함 E.164 만. */
export function isValidPhone(value) {
  const phone = normalizePhone(value);

  if (phone === '') return false;

  if (phone.startsWith('+')) {
    return /^\+[1-9]\d{6,14}$/.test(phone);
  }

  return /^01[016789]\d{7,8}$/.test(phone);
}

export const PHONE_FORMAT_MESSAGE =
  '한국 번호는 01012345678, 해외 번호는 국가번호를 포함해 입력해 주세요 (예: +14155550123)';

export function createPhoneUtils() {
  return {
    sanitizeInput: sanitizePhoneInput,
    normalize: normalizePhone,
    isValid: isValidPhone,
    message: PHONE_FORMAT_MESSAGE,
    maxLength: MAX_LENGTH,
  };
}
